import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {Component, Input, OnDestroy, OnInit, Optional} from "@angular/core";
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MatDialogRef} from "@angular/material/dialog";
import {Subscription} from "rxjs";
import moment from "moment";
import {PaymentReconciliationFilter} from "../../models/client-reconciliation";
import {AdxPaymentService} from "../../service/adx-payment.service";

@Component({
    selector: 'payments-reconciliation-filter',
    templateUrl: './payments-reconciliation-filter.component.html',
    styleUrls: ['./payments-reconciliation-filter.component.scss'],
    standalone: false
})
export class PaymentReconciliationFilterComponent implements OnInit, OnDestroy {
    @Input() config!: PaymentReconciliationFilter;

    paymentsReconFilterForm!: FormGroup;

    breakPoint$?:Subscription;
    isSmall: boolean = false;

    constructor(
        @Optional() public paymentsReconDialogRef: MatDialogRef<PaymentReconciliationFilterComponent>,
        breakpointObserver: BreakpointObserver,
        private formBuilder:FormBuilder,
        private adxPaymentService: AdxPaymentService
    ) {
        this.paymentsReconFilterForm = this.formBuilder.group({
            reconType: null,
            processorIds: [[1, 2], Validators.required],
            adxReference: null,
            lastFourCC: null,
            isMatched: null,
            releaseDateFrom: null,
            releaseDateTo: null,
        });

        this.breakPoint$ = breakpointObserver.observe([
            Breakpoints.Medium,
            Breakpoints.Small,
            Breakpoints.XSmall
        ]).subscribe(result => {
            this.isSmall = result.matches;
        });
    }

    ngOnInit(): void {
        this.adxPaymentService.getPaymentsReconciliationFilter().subscribe((filter: PaymentReconciliationFilter | {}) => {
            if (filter && Object.keys(filter).length > 0) {
                this.config = filter as PaymentReconciliationFilter;
                this.patchFormValues();
            }
        });
    }

    patchFormValues(): void {
        if (!this.config) return;

        let adxRefValue: any = null;
        let lastFourValue: any = null;

        if (this.config.adxReference) {
            if (typeof this.config.adxReference === 'string') {
                adxRefValue = {
                    id: 0,
                    value: this.config.adxReference,
                    display: this.config.adxReference,
                    name: this.config.adxReference,
                    text: this.config.adxReference
                };
            } else {
                adxRefValue = this.config.adxReference;
            }
        }

        if (this.config.lastFourCC) {
            if (typeof this.config.lastFourCC === 'string' || typeof this.config.lastFourCC === 'number') {
                const strValue = String(this.config.lastFourCC);
                lastFourValue = {
                    id: 0,
                    value: strValue,
                    display: strValue,
                    name: strValue,
                    text: strValue
                };
            } else {
                lastFourValue = this.config.lastFourCC;
            }
        }

        this.paymentsReconFilterForm.patchValue({
            reconType: this.config.reconType || null,
            processorIds: this.config.processorIds || [1, 2],
            adxReference: adxRefValue,
            lastFourCC: lastFourValue,
            isMatched: this.config.isMatched !== null && this.config.isMatched !== undefined ? this.config.isMatched : null,
            releaseDateFrom: this.config.releaseDateFrom ? new Date(this.config.releaseDateFrom) : null,
            releaseDateTo: this.config.releaseDateTo ? new Date(this.config.releaseDateTo) : null,
        });
    }

    ngOnDestroy(): void {
        if (this.breakPoint$) {
            this.breakPoint$.unsubscribe();
        }
    }

    applyFilters(): void {
        const formValues = this.paymentsReconFilterForm.value;

        let adxReferenceValue = null;
        if (formValues.adxReference) {
            if (typeof formValues.adxReference === 'object' && formValues.adxReference !== null) {
                if (formValues.adxReference.value) {
                    adxReferenceValue = formValues.adxReference.value;
                } else if (formValues.adxReference.name) {
                    adxReferenceValue = formValues.adxReference.name;
                } else if (formValues.adxReference.text) {
                    adxReferenceValue = formValues.adxReference.text;
                } else if (formValues.adxReference.display) {
                    adxReferenceValue = formValues.adxReference.display;
                } else if (formValues.adxReference.toString) {
                    adxReferenceValue = formValues.adxReference.toString();
                }
            } else if (typeof formValues.adxReference === 'string') {
                adxReferenceValue = formValues.adxReference.trim();
            } else if (formValues.adxReference !== null) {
                adxReferenceValue = String(formValues.adxReference);
            }
        }

        let lastFourCCValue = null;
        if (formValues.lastFourCC) {
            if (typeof formValues.lastFourCC === 'object' && formValues.lastFourCC !== null) {
                if (formValues.lastFourCC.value) {
                    lastFourCCValue = formValues.lastFourCC.value;
                } else if (formValues.lastFourCC.text) {
                    lastFourCCValue = formValues.lastFourCC.text;
                } else if (formValues.lastFourCC.toString) {
                    lastFourCCValue = formValues.lastFourCC.toString();
                }
            } else if (typeof formValues.lastFourCC === 'string') {
                lastFourCCValue = formValues.lastFourCC.trim();
            } else if (typeof formValues.lastFourCC === 'number') {
                lastFourCCValue = formValues.lastFourCC.toString();
            } else if (formValues.lastFourCC !== null) {
                lastFourCCValue = String(formValues.lastFourCC);
            }
        }

        if (lastFourCCValue !== null) {
            lastFourCCValue = String(lastFourCCValue);
        }

        const updatedFilter: PaymentReconciliationFilter = {
            reconType: formValues.reconType || null,
            processorIds: formValues.processorIds || [1, 2],
            adxReference: adxReferenceValue,
            lastFourCC: lastFourCCValue,
            isMatched: formValues.isMatched,
            releaseDateFrom: formValues.releaseDateFrom ? moment(formValues.releaseDateFrom).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null,
            releaseDateTo: formValues.releaseDateTo ? moment(formValues.releaseDateTo).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') : null
        };

        this.adxPaymentService.setPaymentsReconciliationFilter(updatedFilter);

        this.paymentsReconDialogRef.close(updatedFilter);
    }

    closeModal(): void {
        if (this.paymentsReconDialogRef) {
            this.paymentsReconDialogRef.close();
        }
    }

    clearFields(...args:string[]): void {
        if(!args.length) {
            this.paymentsReconFilterForm.reset();
            this.paymentsReconFilterForm.patchValue({
                processorIds: [1, 2]
            });
            const defaultFilter: PaymentReconciliationFilter = {
                processorIds: [1, 2],
                reconType: null,
                adxReference: null,
                lastFourCC: null,
                isMatched: null,
                releaseDateFrom: null,
                releaseDateTo: null
            };
            this.adxPaymentService.setPaymentsReconciliationFilter(defaultFilter);
            return;
        }
        args.forEach((field) => {
            this.paymentsReconFilterForm.controls[field].reset();
            if (field === 'processorIds') {
                this.paymentsReconFilterForm.controls[field].setValue([1, 2]);
            }
        });
    }

    checkInput(event: KeyboardEvent): void {
        const charCode = event.charCode;
        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
        }
    }

    displayAutocompleteFn(option: any): string {
        if (!option) return '';

        if (typeof option === 'string') {
            return option;
        } else if (typeof option === 'object') {
            return option.display || option.value || option.name || option.text || option.toString();
        }

        return String(option);
    }
}
